import { DEFAULT_FILTER_IDS, PerformanceAudienceDateRange } from '@/services/endpoints/performanceAudience';

export type ResolvedDateRange = {
  from: string;
  to: string;
};

export const MAX_CUSTOM_DATE_RANGE_DAYS = 365;

export const formatIsoDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

export const parseIsoDate = (iso: string): Date => {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, month - 1, day);
};

export const formatDisplayDate = (date: Date, locale?: string) => {
  return date.toLocaleDateString(locale || undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

export const formatDisplayDateRange = (fromIso: string, toIso: string, locale?: string) => {
  return `${formatDisplayDate(parseIsoDate(fromIso), locale)} – ${formatDisplayDate(parseIsoDate(toIso), locale)}`;
};

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const addDays = (date: Date, days: number) => {
  const next = startOfDay(date);
  next.setDate(next.getDate() + days);
  return next;
};

const startOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);

const endOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth() + 1, 0);

const startOfQuarter = (date: Date) => {
  const quarterStartMonth = Math.floor(date.getMonth() / 3) * 3;
  return new Date(date.getFullYear(), quarterStartMonth, 1);
};

export const inclusiveDayCount = (from: Date, to: Date) => {
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.floor((startOfDay(to).getTime() - startOfDay(from).getTime()) / msPerDay) + 1;
};

export const inclusiveDayCountFromIso = (from: string, to: string) =>
  inclusiveDayCount(parseIsoDate(from), parseIsoDate(to));

export const isValidCustomDateRange = (from: string, to: string): boolean => {
  const fromDate = parseIsoDate(from);
  const toDate = parseIsoDate(to);

  if (Number.isNaN(fromDate.getTime()) || Number.isNaN(toDate.getTime())) {
    return false;
  }

  if (fromDate > toDate) {
    return false;
  }

  return inclusiveDayCount(fromDate, toDate) <= MAX_CUSTOM_DATE_RANGE_DAYS;
};

export const resolveDateRangePreset = (
  presetId: string | undefined,
  now: Date = new Date()
): ResolvedDateRange => {
  const today = startOfDay(now);
  const preset = (presetId as PerformanceAudienceDateRange | undefined) ?? DEFAULT_FILTER_IDS.dateRange;

  switch (preset) {
    case 'today':
      return { from: formatIsoDate(today), to: formatIsoDate(today) };
    case 'yesterday': {
      const yesterday = addDays(today, -1);
      return { from: formatIsoDate(yesterday), to: formatIsoDate(yesterday) };
    }
    case 'last_14_days':
      return { from: formatIsoDate(addDays(today, -13)), to: formatIsoDate(today) };
    case 'last_30_days':
      return { from: formatIsoDate(addDays(today, -29)), to: formatIsoDate(today) };
    case 'current_month':
      return { from: formatIsoDate(startOfMonth(today)), to: formatIsoDate(today) };
    case 'last_month': {
      const previousMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      return { from: formatIsoDate(startOfMonth(previousMonth)), to: formatIsoDate(endOfMonth(previousMonth)) };
    }
    case 'current_quarter':
      return { from: formatIsoDate(startOfQuarter(today)), to: formatIsoDate(today) };
    case 'last_7_days':
    case 'custom':
    default:
      return { from: formatIsoDate(addDays(today, -6)), to: formatIsoDate(today) };
  }
};

export type DateRangeFilterInput = {
  dateRange?: string;
  dateFrom?: string;
  dateTo?: string;
};

export const resolveFiltersDateRange = (filters: DateRangeFilterInput, now: Date = new Date()): ResolvedDateRange => {
  if (filters.dateRange === 'custom' && filters.dateFrom && filters.dateTo && isValidCustomDateRange(filters.dateFrom, filters.dateTo)) {
    return { from: filters.dateFrom, to: filters.dateTo };
  }

  return resolveDateRangePreset(filters.dateRange, now);
};

export const getDateRangePresetDayCount = (presetId: string | undefined, now: Date = new Date()): number => {
  const { from, to } = resolveDateRangePreset(presetId, now);
  return inclusiveDayCountFromIso(from, to);
};

export const getDateRangeDayCount = (filters: DateRangeFilterInput, now: Date = new Date()): number => {
  const { from, to } = resolveFiltersDateRange(filters, now);
  return inclusiveDayCountFromIso(from, to);
};
